import { useEffect, useRef } from 'react';
import { tablistKeys } from './index';

export interface TabItem<T extends string> {
  id: T;
  label: string;
  /** Shown beside the label once known. */
  count?: number;
  /** An empty tab of a filter: there is nothing to choose. */
  disabled?: boolean;
}

/**
 * A record's row of tabs (TF-49, TF-252): the timeline's filters and the record's people, deals and
 * files, each with its count. Arrow keys, Home and End move between them (tablistKeys); on a narrow
 * screen the row scrolls sideways and keeps the chosen tab in view. Every record page uses this one.
 */
export function Tabs<T extends string>({
  tabs,
  value,
  onChange,
  label,
  idPrefix,
  panelId,
}: {
  tabs: TabItem<T>[];
  value: T;
  onChange: (tab: T) => void;
  label: string;
  /** Each tab's id is `${idPrefix}-${tab}`, for the panel's aria-labelledby. */
  idPrefix: string;
  panelId: string;
}) {
  const row = useRef<HTMLDivElement>(null);
  // The chosen tab in sight in a row that scrolls (a phone, a link straight to the last tab), again
  // when the counts arrive and widen the tabs. Only the row scrolls, never the page: scrollIntoView
  // would move the page to the row too.
  const widths = tabs.map((tab) => `${tab.id}:${tab.count ?? ''}`).join();
  useEffect(() => {
    const list = row.current;
    const tab = list?.querySelector<HTMLElement>('[aria-selected="true"]');
    if (!list || !tab || list.scrollWidth <= list.clientWidth) return;
    const start = tab.getBoundingClientRect().left - list.getBoundingClientRect().left + list.scrollLeft;
    const end = start + tab.offsetWidth;
    if (start < list.scrollLeft) list.scrollLeft = start;
    else if (end > list.scrollLeft + list.clientWidth) list.scrollLeft = end - list.clientWidth;
  }, [value, widths]);

  return (
    <div ref={row} className="timeline-tabs" role="tablist" aria-label={label} onKeyDown={tablistKeys}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          id={`${idPrefix}-${tab.id}`}
          type="button"
          role="tab"
          aria-selected={tab.id === value}
          aria-controls={panelId}
          tabIndex={tab.id === value ? 0 : -1}
          disabled={tab.disabled}
          className={tab.id === value ? 'timeline-tab active' : 'timeline-tab'}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
          {tab.count !== undefined && <span className="count">{tab.count}</span>}
        </button>
      ))}
    </div>
  );
}
